import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from 'date-fns';
import RecordDetailsModal from './RecordDetailsPage';
import { Dialog } from 'primereact/dialog';
import { FaArrowLeft } from "react-icons/fa";
import secureStorage from "../utils/secureStorage";
import { validateToken } from "../tokenValidation";


export default function AbhaRecords() {
  const API_URL = process.env.REACT_APP_API_URL;
  const location = useLocation();
  const navigate = useNavigate();
  const [tab2Records, setTab2Records] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [jumpPage, setJumpPage] = useState("");
  const [ocrModal, setOCRModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const queryParams = new URLSearchParams(location.search);
  const queryParam = queryParams.get("queryParam");

  useEffect(() => {
    validateToken(navigate);
  }, [navigate]);

  const fetchTab2Records = async () => {
    setLoading(true);
    try {
      const token = secureStorage.get("authToken");
      const clinicId = secureStorage.get("clinic");
      if (!token) {
        console.error("Authentication token not found. Please log in again.");
        return;
      }

      const response = await fetch(
        `${API_URL}hospital/get-abha-records?consentRequestId=${queryParam}&pageNumber=${pageNumber}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-HIP-ID":clinicId
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setTab2Records(result.data.content || []);
        setTotalPages(result.data.totalPages);
        setTotalRecords(result.data.totalElements);
      } else {
        console.error("Failed to fetch Tab 2 records:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching Tab 2 records:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setOCRModal(true);
  };

  const handleCloseRecord = () => {
    setOCRModal(false);
    setSelectedRecord(null);
  };

  useEffect(() => {
    const pageNumberFromState = location.state?.pageNumber;
    setPageNumber(pageNumberFromState !== undefined ? pageNumberFromState : 0);
  }, [location.state]);

  useEffect(() => {
    fetchTab2Records();
  }, [pageNumber]);

  const handleNext = () => {
    if (pageNumber + 1 < totalPages) setPageNumber((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (pageNumber > 0) setPageNumber((prev) => prev - 1);
  };

  const handleJumpToPage = () => {
    const page = parseInt(jumpPage, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      setPageNumber(page - 1);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between m-4 mb-6 relative">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-0 flex items-center text-teal-700 hover:text-teal-900 font-medium"
        >
          <FaArrowLeft className="mr-2" /> Back
        </button>

        <h1 className="w-full text-center text-3xl font-bold text-gray-800">Patient Records</h1>
      </div>

      <div className="rounded-lg overflow-hidden shadow-md" style={{ backgroundColor: "#DEEBEC" }}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-40">
            <div className="w-16 h-16 border-4 border-t-teal-600 border-gray-300 rounded-full animate-spin"></div>
          </div>
        ) : tab2Records.length === 0 ? (
          <p className="text-center py-4 text-gray-700">No ABHA health records found</p>
        ) : (
          <>
            <table className="w-full table-auto">
              <thead className="bg-[#DEEBEC] border-b border-[#0F766E]">
                <tr>
                  <th className="px-4 py-3 text-left text-[15px] font-semibold text-[#0F766E]">SL No</th>
                  <th className="px-4 py-3 text-left text-[15px] font-semibold text-[#0F766E]">Facility Name</th>
                  <th className="px-4 py-3 text-left text-[15px] font-semibold text-[#0F766E]">Record Type</th>
                  <th className="px-4 py-3 text-left text-[15px] font-semibold text-[#0F766E]">Care Context Reference</th>
                  <th className="px-4 py-3 text-left text-[15px] font-semibold text-[#0F766E]">Visit Date</th>
                  <th className="px-4 py-3 text-left text-[15px] font-semibold text-[#0F766E]">View</th>
                </tr>
              </thead>
              <tbody>
                {tab2Records.map((record, index) => (
                  <tr key={index} className="bg-[#DEEBEC] border-b border-[#C6D9DB]">
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{index + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{record.facilityName}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{record.documentType}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{record.careContextReference}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">
                      {record.visitDate
                        ? format(
                          new Date(record.visitDate.replace("DateTimeType[", "").replace("]", "")),
                          "dd-MMM-yyyy hh:mma"
                        )
                        : "N/A"}
                    </td>
                    <td
                      className="px-4 py-3 text-[#2B98D6] font-medium hover:underline cursor-pointer"
                      onClick={() => handleViewRecord(record)}
                    >
                      View
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Controls */}
            <div className="flex justify-between items-center p-6 flex-wrap gap-2">
              {/* Left side: Page status + dropdown */}
              <div className="flex items-center gap-2 text-gray-800 font-medium">
                <span>Page</span>
                <select
                  value={pageNumber + 1}
                  onChange={(e) => setPageNumber(Number(e.target.value) - 1)}
                  className="px-2 py-1 border rounded-md text-gray-700"
                >
                  {Array.from({ length: totalPages }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
                <span>of {totalPages}</span>
              </div>

              {/* Right side: Prev/Next */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevious}
                  disabled={pageNumber === 0}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={handleNext}
                  disabled={pageNumber + 1 >= totalPages}
                  className="bg-teal-500 text-white px-4 py-2 rounded-md hover:bg-teal-600"
                >
                  Next
                </button>
              </div>
            </div>

          </>
        )}

        <Dialog
          header={selectedRecord?.documentType || "Record"}
          visible={ocrModal}
          onHide={handleCloseRecord}
          style={{ width: "90vw" }}
          breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        >
          <RecordDetailsModal record={selectedRecord} onClose={handleCloseRecord} />
        </Dialog>
      </div>
    </>
  );
}
